import React, { useState, useCallback, useMemo } from "react";
import {
    Stack,
    Text,
    PrimaryButton,
    DefaultButton,
    Spinner,
    SpinnerSize,
    mergeStyles,
    Dialog,
    DialogType,
    DialogFooter,
    ProgressIndicator
} from "@fluentui/react";
import {
    Box,
    Paper,
    Typography,
    Chip,
    Alert,
    Grid,
    Snackbar
} from "@mui/material";
import {
    Database20Regular,
    Shield20Regular,
    People20Regular,
    ArrowSync20Regular,
    Warning20Regular,
    LockClosed20Regular,
    Server20Regular,
    StorageRegular,
    Apps20Regular,
    DataBarVertical20Regular,
    ChartMultiple20Regular,
    PersonSupport20Regular,
    Lightbulb20Regular,
    Handshake20Regular,
    Building20Regular,
    PeopleTeam20Regular,
    ContactCard20Regular
} from "@fluentui/react-icons";
import {
    CACHE_SERVICES,
    CacheService,
    isUserAuthorizedForCacheAdmin
} from "../config/CacheAdminConfig";
import {
    useClearCacheByEndpointMutation
} from "../slices/CacheAdmin.slice";
import AdminActivityMenu from "../components/AdminActivityMenu";

// Theme colors
const THEME = {
    primary: "#0078D4",
    background: "#F5F7FA",
    surface: "#FFFFFF",
    textPrimary: "#1A1A2E",
    textSecondary: "#5A5A72",
    border: "#E1E5EB",
    success: "#107C10",
    warning: "#FFB900",
    error: "#D13438",
    successBg: "#DFF6DD",
    warningBg: "#FFF4CE",
    errorBg: "#FDE7E9",
    infoBg: "#E5F1FB"
};

// Styles
const pageContainerStyle = mergeStyles({
    padding: "24px 32px",
    backgroundColor: THEME.background,
    minHeight: "calc(100vh - 100px)"
});

const sectionStyle = mergeStyles({
    backgroundColor: THEME.surface,
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
});

const compactCardStyle = mergeStyles({
    backgroundColor: THEME.surface,
    borderRadius: "8px",
    border: `1px solid ${THEME.border}`,
    padding: "14px",
    minHeight: "160px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    transition: "all 0.2s ease",
    ":hover": {
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        borderColor: THEME.primary
    }
});

const dangerCardStyle = mergeStyles({
    backgroundColor: "#FFF5F5",
    borderRadius: "8px",
    border: `2px solid ${THEME.error}`,
    padding: "14px",
    transition: "all 0.2s ease",
    ":hover": {
        boxShadow: "0 4px 12px rgba(209, 52, 56, 0.2)"
    }
});

const getIconForService = (icon: string, color?: string): JSX.Element => {
    const iconProps = { style: { fontSize: 18, color: color || THEME.primary, flexShrink: 0 } };
    switch (icon) {
        case "Database": return <Database20Regular {...iconProps} />;
        case "Shield": return <Shield20Regular {...iconProps} />;
        case "People": return <People20Regular {...iconProps} />;
        case "Sync": return <ArrowSync20Regular {...iconProps} />;
        case "Server": return <Server20Regular {...iconProps} />;
        case "ProductVariant":
        case "ClearFormatting": return <Apps20Regular {...iconProps} />;
        case "BarChart": return <DataBarVertical20Regular {...iconProps} />;
        case "Chart": return <ChartMultiple20Regular {...iconProps} />;
        case "Support": return <PersonSupport20Regular {...iconProps} />;
        case "Lightbulb": return <Lightbulb20Regular {...iconProps} />;
        case "Partnership": return <Handshake20Regular {...iconProps} />;
        case "Building": return <Building20Regular {...iconProps} />;
        case "PeopleGroup": return <PeopleTeam20Regular {...iconProps} />;
        case "CRM": return <ContactCard20Regular {...iconProps} />;
        default: return <StorageRegular {...iconProps} />;
    }
};

const getCacheTypeChip = (type: "redis" | "memory" | "both") => {
    const config = {
        redis: { label: "Redis", color: "#C62828", bgColor: "#FFEBEE" },
        memory: { label: "Memory", color: "#6A1B9A", bgColor: "#F3E5F5" },
        both: { label: "Both", color: "#1565C0", bgColor: "#E3F2FD" }
    };
    const { label, color, bgColor } = config[type];
    return (
        <Chip
            label={label}
            size="small"
            sx={{ backgroundColor: bgColor, color: color, fontWeight: 600, fontSize: "10px", height: 20, flexShrink: 0 }}
        />
    );
};

interface ClearCacheResult {
    serviceId: string;
    serviceName: string;
    success: boolean;
    message: string;
    timestamp: Date;
}

// Compact Cache Card Component
const CacheCard: React.FC<{
    service: CacheService;
    onClear: () => void;
    isClearing: boolean;
    disabled: boolean;
}> = ({ service, onClear, isClearing, disabled }) => (
    <Box className={compactCardStyle}>
        {/* Header Row */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1, flexShrink: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1, overflow: "hidden" }}>
                {getIconForService(service.icon)}
                <Typography variant="body2" sx={{ fontWeight: 600, color: THEME.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {service.name}
                </Typography>
            </Box>
            {getCacheTypeChip(service.type)}
        </Box>

        {/* Description */}
        <Typography variant="caption" sx={{ color: THEME.textSecondary, display: "block", mb: 1, lineHeight: 1.3, flexShrink: 0, height: "30px", overflow: "hidden" }}>
            {service.description}
        </Typography>

        {/* Affected Pages */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1, overflow: "hidden", maxHeight: "24px", flex: 1 }}>
            {service.affectedPages?.slice(0, 2).map((page, idx) => (
                <Chip
                    key={idx}
                    label={page}
                    size="small"
                    sx={{ backgroundColor: "#F0F7FF", color: THEME.primary, fontSize: "9px", height: 18 }}
                />
            ))}
            {service.affectedPages && service.affectedPages.length > 2 && (
                <Chip label={`+${service.affectedPages.length - 2}`} size="small" sx={{ backgroundColor: "#F0F0F0", fontSize: "9px", height: 18 }} />
            )}
        </Box>

        {/* Clear Button - pushed to bottom */}
        <Box sx={{ mt: "auto", pt: 1, flexShrink: 0 }}>
            <PrimaryButton
                text={isClearing ? "..." : "Clear"}
                onClick={onClear}
                disabled={disabled}
                styles={{
                    root: { height: 28, minWidth: 60, width: "100%", backgroundColor: THEME.primary, fontSize: 12 }
                }}
            />
        </Box>
    </Box>
);

const CacheAdminPage: React.FC = () => {
    const userEmail = sessionStorage.getItem("loginUserName");
    const isAuthorized = useMemo(() => isUserAuthorizedForCacheAdmin(userEmail), [userEmail]);

    const [clearCache] = useClearCacheByEndpointMutation();

    const [clearingServiceId, setClearingServiceId] = useState<string | null>(null);
    const [results, setResults] = useState<ClearCacheResult[]>([]);
    const [confirmDialog, setConfirmDialog] = useState<{ isOpen: boolean; service: CacheService | null }>({ isOpen: false, service: null });
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({ open: false, message: "", severity: "info" });

    // Group services
    const groupedServices = useMemo(() => {
        const productServices = CACHE_SERVICES.filter(s => s.group === "product");
        const healthServices = CACHE_SERVICES.filter(s => s.group === "health");
        const customerServices = CACHE_SERVICES.filter(s => s.group === "customer");
        const otherServices = CACHE_SERVICES.filter(s => s.group === "other");
        const dangerServices = CACHE_SERVICES.filter(s => s.group === "danger");
        return { productServices, healthServices, customerServices, otherServices, dangerServices };
    }, []);

    const handleClearCache = useCallback(async (service: CacheService) => {
        setClearingServiceId(service.id);
        try {
            const method = service.apiEndpoint.includes("removeAllRedisCacheAtOnce") ? "GET" : "POST";
            await clearCache({ endpoint: service.apiEndpoint, method }).unwrap();
            setResults(prev => [{ serviceId: service.id, serviceName: service.name, success: true, message: "Cleared", timestamp: new Date() }, ...prev.slice(0, 4)]);
            setSnackbar({ open: true, message: `${service.name} cleared!`, severity: "success" });
        } catch {
            setResults(prev => [{ serviceId: service.id, serviceName: service.name, success: false, message: "Failed", timestamp: new Date() }, ...prev.slice(0, 4)]);
            setSnackbar({ open: true, message: `Failed to clear ${service.name}`, severity: "error" });
        } finally {
            setClearingServiceId(null);
            setConfirmDialog({ isOpen: false, service: null });
        }
    }, [clearCache]);

    const openConfirmDialog = useCallback((service: CacheService) => {
        setConfirmDialog({ isOpen: true, service });
    }, []);

    // Access Denied
    if (!isAuthorized) {
        return (
            <div className={pageContainerStyle}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", textAlign: "center" }}>
                    <LockClosed20Regular style={{ fontSize: 64, color: THEME.error, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ color: THEME.textPrimary, fontWeight: 600, mb: 1 }}>Access Denied</Typography>
                    <Typography variant="body2" sx={{ color: THEME.textSecondary, maxWidth: 400, mb: 2 }}>
                        This page is restricted to authorized administrators.
                    </Typography>
                    <Alert severity="warning" sx={{ maxWidth: 400 }}>Contact your system administrator for access.</Alert>
                </Box>
            </div>
        );
    }

    return (
        <div className={pageContainerStyle}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ color: THEME.textPrimary, fontWeight: 700 }}>Cache Administration</Typography>
                    <Typography variant="body2" sx={{ color: THEME.textSecondary }}>Manage caches • {userEmail}</Typography>
                </Box>
                <DefaultButton iconProps={{ iconName: "Refresh" }} text="Refresh" onClick={() => window.location.reload()} />
            </Box>

            {/* Admin Activity Menu */}
            <Box sx={{ mb: 3 }}>
                <AdminActivityMenu />
            </Box>

            {/* Recent Result */}
            {results.length > 0 && (
                <Alert severity={results[0]?.success ? "success" : "error"} sx={{ mb: 2 }}>
                    <strong>{results[0]?.serviceName}</strong>: {results[0]?.message}
                </Alert>
            )}

            {/* Product View Caches */}
            <div className={sectionStyle}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Apps20Regular style={{ color: THEME.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: THEME.textPrimary }}>Product View Caches</Typography>
                    <Chip label={groupedServices.productServices.length} size="small" sx={{ backgroundColor: THEME.infoBg, color: THEME.primary, fontSize: 10, height: 20 }} />
                </Box>
                <Grid container spacing={2}>
                    {groupedServices.productServices.map((service) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={service.id}>
                            <CacheCard service={service} onClear={() => openConfirmDialog(service)} isClearing={clearingServiceId === service.id} disabled={clearingServiceId !== null} />
                        </Grid>
                    ))}
                </Grid>
            </div>

            {/* Other Page Caches */}
            <div className={sectionStyle}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <StorageRegular style={{ color: THEME.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: THEME.textPrimary }}>Other Page Caches</Typography>
                </Box>
                <Grid container spacing={2}>
                    {[...groupedServices.healthServices, ...groupedServices.customerServices, ...groupedServices.otherServices].map((service) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={service.id}>
                            <CacheCard service={service} onClear={() => openConfirmDialog(service)} isClearing={clearingServiceId === service.id} disabled={clearingServiceId !== null} />
                        </Grid>
                    ))}
                </Grid>
            </div>

            {/* Danger Zone */}
            <Paper elevation={0} sx={{ p: 2, border: `2px solid ${THEME.error}`, borderRadius: "12px", backgroundColor: "#FFF8F8" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Warning20Regular style={{ color: THEME.error }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: THEME.error }}>Danger Zone</Typography>
                </Box>
                <Grid container spacing={1.5}>
                    {groupedServices.dangerServices.map((service) => (
                        <Grid item xs={12} sm={6} md={4} key={service.id}>
                            <Box className={dangerCardStyle}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    {getIconForService(service.icon, THEME.error)}
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: THEME.error }}>{service.name}</Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: THEME.textSecondary, display: "block", mb: 1.5, lineHeight: 1.4 }}>
                                    {service.description}
                                </Typography>
                                <PrimaryButton
                                    text={clearingServiceId === service.id ? "Clearing..." : "Clear All"}
                                    iconProps={{ iconName: "Warning" }}
                                    onClick={() => openConfirmDialog(service)}
                                    disabled={clearingServiceId !== null}
                                    styles={{ root: { backgroundColor: THEME.error, borderColor: THEME.error }, rootHovered: { backgroundColor: "#B71C1C" } }}
                                />
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Confirmation Dialog */}
            <Dialog
                hidden={!confirmDialog.isOpen}
                onDismiss={() => setConfirmDialog({ isOpen: false, service: null })}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: `Clear ${confirmDialog.service?.name}?`,
                    subText: confirmDialog.service?.id === "all-redis"
                        ? "This will clear ALL cached data. The application may be slower until caches rebuild."
                        : `Cache will be refreshed on next request.`
                }}
                modalProps={{ isBlocking: true }}
            >
                {clearingServiceId && <ProgressIndicator label="Clearing..." />}
                <DialogFooter>
                    <DefaultButton onClick={() => setConfirmDialog({ isOpen: false, service: null })} text="Cancel" disabled={clearingServiceId !== null} />
                    <PrimaryButton
                        onClick={() => confirmDialog.service && handleClearCache(confirmDialog.service)}
                        text="Clear"
                        disabled={clearingServiceId !== null}
                        styles={{ root: { backgroundColor: confirmDialog.service?.id === "all-redis" ? THEME.error : THEME.primary } }}
                    />
                </DialogFooter>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default CacheAdminPage;
